import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Undo2, Redo2, Minus,
} from 'lucide-react';
import { toast } from 'sonner';

const ToolBtn = ({ onClick, active, label, children, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={label}
    aria-label={label}
    data-testid={testId}
    className={`p-2 rounded-md transition-colors ${active ? 'bg-[#3B1C73] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
  >
    {children}
  </button>
);

const Toolbar = ({ editor, onPickImage }) => {
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (leave empty to remove)', prev);
    if (url === null) return;
    if (!url) return c().unsetLink().run();
    c().extendMarkRange('link').setLink({ href: /^https?:\/\//.test(url) ? url : `https://${url}` }).run();
  };
  const items = [
    ['bold', 'Bold', Bold, () => c().toggleBold().run(), editor.isActive('bold')],
    ['italic', 'Italic', Italic, () => c().toggleItalic().run(), editor.isActive('italic')],
    ['underline', 'Underline', Underline, () => c().toggleUnderline().run(), editor.isActive('underline')],
    ['h2', 'Heading', Heading2, () => c().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 })],
    ['h3', 'Sub-heading', Heading3, () => c().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 })],
    ['bullet', 'Bullet list', List, () => c().toggleBulletList().run(), editor.isActive('bulletList')],
    ['ordered', 'Numbered list', ListOrdered, () => c().toggleOrderedList().run(), editor.isActive('orderedList')],
    ['quote', 'Quote', Quote, () => c().toggleBlockquote().run(), editor.isActive('blockquote')],
    ['hr', 'Divider', Minus, () => c().setHorizontalRule().run(), false],
    ['link', 'Link', Link2, setLink, editor.isActive('link')],
    ['image', 'Insert image', ImagePlus, onPickImage, false],
    ['undo', 'Undo', Undo2, () => c().undo().run(), false],
    ['redo', 'Redo', Redo2, () => c().redo().run(), false],
  ];
  return (
    <div className="flex flex-wrap gap-0.5 border-b border-slate-200 p-1.5 bg-slate-50 sticky top-0 z-10 rounded-t-xl">
      {items.map(([key, label, Icon, fn, active]) => (
        <ToolBtn key={key} label={label} onClick={fn} active={active} testId={`editor-tool-${key}`}>
          <Icon className="w-4 h-4" />
        </ToolBtn>
      ))}
    </div>
  );
};

export const RichTextEditor = ({ value, onChange, onUploadImage }) => {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-xl my-4' } }),
      Placeholder.configure({ placeholder: 'Start writing your blog… Use the toolbar for headings, lists and images, or just type simple paragraphs.' }),
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML().replace(/(<p><\/p>)+$/, '')),
    editorProps: { attributes: { class: 'blog-content min-h-[320px] px-4 py-3 focus:outline-none', 'data-testid': 'blog-content-editor' } },
  });

  useEffect(() => {
    if (editor && value && editor.isEmpty) editor.commands.setContent(value);
  }, [editor, value]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const url = await onUploadImage(file);
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    }
  };

  if (!editor) return null;
  return (
    <div className="border border-slate-200 rounded-xl bg-white">
      <Toolbar editor={editor} onPickImage={() => fileRef.current?.click()} />
      <EditorContent editor={editor} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="editor-image-input" />
    </div>
  );
};
